import { useState } from 'react';
import { ChevronDown, ChevronRight, MailWarning, Send } from 'lucide-react';
import type { OverdueClientGroup, OverdueInvoicesResult } from '../../lib/dashboardApi';
import { formatDate, relativeTime } from '../../lib/format';
import { useI18n } from '../../contexts/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { SendReminderModal } from './SendReminderModal';

interface OverdueInvoicesPanelProps {
  data: OverdueInvoicesResult;
}

function formatAmount(amount: number, currency: string): string {
  return `${new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)} ${currency}`;
}

/**
 * Overdue invoices grouped per client (past due and not yet collected: SmartBill
 * UNPAID/PARTIAL, e-Factura SPV invoices not marked collected, Facturi rows).
 * Each group expands into its invoice list; clients matched to the CRM
 * (clientId + email) get a "send reminder" button, gated by
 * financials.canSendReminders.
 */
export function OverdueInvoicesPanel({ data }: OverdueInvoicesPanelProps) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reminderClient, setReminderClient] = useState<OverdueClientGroup | null>(null);

  const canSend = can('financials', 'canSendReminders');

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MailWarning size={16} className="text-red-400" aria-hidden />
          {t('dashboard.overdue.title')}
        </h3>
        {data.clients.length > 0 && (
          <span className="text-sm font-semibold text-red-400">
            {formatAmount(data.totalOverdue, data.clients[0]?.currency ?? 'RON')}
          </span>
        )}
      </div>

      {data.clients.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">{t('dashboard.overdue.empty')}</p>
      ) : (
        <ul className="mt-4 space-y-1">
          {data.clients.map((group) => {
            const key = group.clientId ?? `partner:${group.clientName}`;
            const isOpen = expanded.has(key);
            const nameToggle = (
              <button
                type="button"
                onClick={() => toggle(key)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
                )}
                <span className="truncate text-sm font-medium text-foreground">
                  {group.clientName}
                </span>
              </button>
            );
            const badges = (short: boolean) => (
              <span className="flex min-w-0 items-center gap-x-2">
                <span className="whitespace-nowrap rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-400">
                  {group.invoiceCount === 1
                    ? t('dashboard.overdue.invoiceCountOne')
                    : t('dashboard.overdue.invoiceCount', { count: String(group.invoiceCount) })}
                </span>
                <span className="whitespace-nowrap text-xs font-medium text-red-400">
                  {t(
                    short ? 'dashboard.overdue.daysOverdueShort' : 'dashboard.overdue.daysOverdue',
                    {
                      days: String(group.maxDaysOverdue),
                    },
                  )}
                </span>
                {!group.clientId && (
                  <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {t('dashboard.overdue.unmatchedClient')}
                  </span>
                )}
              </span>
            );
            const amount = (
              <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-foreground">
                {formatAmount(group.totalOverdue, group.currency)}
              </span>
            );
            // Compact: no taller than the badges, so rows with and without the
            // button keep the same height.
            const reminderButton = (short: boolean) =>
              canSend && group.clientId && group.email ? (
                <button
                  type="button"
                  onClick={() => setReminderClient(group)}
                  className="ui-btn-secondary shrink-0 whitespace-nowrap !gap-1 !px-2 !py-0.5 text-xs"
                >
                  <Send size={12} aria-hidden />
                  {t(
                    short
                      ? 'dashboard.overdue.sendReminderShort'
                      : 'dashboard.overdue.sendReminder',
                  )}
                </button>
              ) : null;
            return (
              <li key={key} className="rounded-lg border border-border/60">
                {/* Phones: two lines of equal height for every client (line 2 is
                    always 44px tall, the touch-target height the reminder button
                    gets from index.css, so rows without it line up too),
                      1. name ............ amount
                      2. badges ...... [reminder]
                    md and up: one row. Nothing here may be wider than the card,
                    or the whole dashboard scrolls sideways. */}
                <div className="space-y-1.5 px-2 py-2 hover:bg-muted/40 md:hidden">
                  <div className="flex items-center gap-3">
                    {nameToggle}
                    {amount}
                  </div>
                  <div className="flex min-h-[44px] items-center justify-between gap-2 pl-[22px]">
                    {badges(true)}
                    {reminderButton(true)}
                  </div>
                </div>
                <div className="hidden items-center gap-3 px-2 py-2 hover:bg-muted/40 md:flex">
                  {nameToggle}
                  <div className="shrink-0">{badges(false)}</div>
                  <div className="hidden whitespace-nowrap text-xs text-muted-foreground lg:block">
                    {group.lastReminderAt
                      ? t('dashboard.overdue.lastReminder', {
                          when: relativeTime(group.lastReminderAt),
                        })
                      : t('dashboard.overdue.noReminder')}
                  </div>
                  <div className="w-28 shrink-0 text-right">{amount}</div>
                  {reminderButton(false)}
                </div>

                {isOpen && (
                  <ul className="border-t border-border/60 px-3 py-2">
                    {group.invoices.map((inv) => (
                      <li
                        key={inv.id}
                        className="flex items-center gap-3 rounded px-1 py-1.5 text-xs hover:bg-muted/30"
                      >
                        <span className="w-24 shrink-0 font-medium text-foreground">
                          {[inv.series, inv.number].filter(Boolean).join(' ')}
                        </span>
                        <span className="hidden w-28 shrink-0 text-muted-foreground sm:block">
                          {formatDate(inv.dueDate)}
                        </span>
                        <span className="shrink-0 text-red-400">
                          {t('dashboard.overdue.daysOverdue', { days: String(inv.daysOverdue) })}
                        </span>
                        <span className="min-w-0 flex-1" />
                        <span className="shrink-0 font-semibold text-foreground">
                          {formatAmount(inv.outstanding, inv.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {reminderClient?.clientId && (
        <SendReminderModal
          open
          clientId={reminderClient.clientId}
          clientName={reminderClient.clientName}
          onClose={() => setReminderClient(null)}
        />
      )}
    </section>
  );
}
